"use client";

import {useMemo} from "react";

import {useI18nContext} from "./i18n-provider";
import type {Dictionary, Locale, Namespace, NamespacedTranslateFunction, NamespaceKeys, TranslateFunction, TranslationKey} from "./types";

/**
 * Return type for useTranslation hook without namespace
 */
interface UseTranslationReturnBase {
    /** Translation function */
    t: TranslateFunction;
    /** Current locale */
    locale: Locale;
    /** Full dictionary (for advanced use cases) */
    dict: Dictionary;
}

/**
 * Return type for useTranslation hook with namespace
 */
interface UseTranslationReturnNamespaced<NS extends Namespace> {
    /** Translation function scoped to namespace */
    t: NamespacedTranslateFunction<NS>;
    /** Current locale */
    locale: Locale;
    /** Full dictionary (for advanced use cases) */
    dict: Dictionary;
}

/**
 * Hook for translations in client components
 *
 * @param  - Optional namespace prefix (e.g., "common", "destination")
 *
 * @example
 * // Without namespace - use full keys
 * function MyComponent() {
 *   const { t } = useTranslation();
 *   return <h1>{t("common.readMore")}</h1>;
 * }
 *
 * @example
 * // With namespace - shorter keys with autocomplete
 * function MyComponent() {
 *   const { t } = useTranslation("common");
 *   return <h1>{t("readMore")}</h1>; // → looks up "common.readMore"
 * }
 *
 * @example
 * // With namespace but full key access using $
 * function MyComponent() {
 *   const { t } = useTranslation("common");
 *   return (
 *     <>
 *       <h1>{t("readMore")}</h1>           // → "common.readMore"
 *       <p>{t("$.destination.welcome")}</p> // → "destination.welcome" (escape namespace)
 *     </>
 *   );
 * }
 */
export function useTranslation(): UseTranslationReturnBase;
export function useTranslation<NS extends Namespace>(namespace: NS): UseTranslationReturnNamespaced<NS>;
export function useTranslation<NS extends Namespace>(namespace?: NS): UseTranslationReturnBase | UseTranslationReturnNamespaced<NS> {
    const {t: rootT, locale, dict} = useI18nContext();

    const t = useMemo(() => {
        if (!namespace) {
            return rootT;
        }

        return (key: NamespaceKeys<NS> | `$.${TranslationKey}`, params?: Record<string, string | number>) => {
            const keyStr = key as string;
            // If key starts with "$." — use absolute path (escape namespace)
            if (keyStr.startsWith("$.")) {
                const absoluteKey = keyStr.slice(2) as TranslationKey;
                return rootT(absoluteKey, params);
            }

            // Otherwise prepend namespace
            const fullKey = `${namespace}.${keyStr}` as TranslationKey;
            return rootT(fullKey, params);
        };
    }, [namespace, rootT]);

    return {t, locale, dict} as unknown as UseTranslationReturnBase | UseTranslationReturnNamespaced<NS>;
}
